import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FileText, List, Grid, Download } from 'lucide-react';
import ColumnSearchHeaderCell, { openOrToggleColumnSearch, clearColumnSearchValue } from '../../components/common/ColumnSearchHeaderCell';
import * as XLSX from 'xlsx';
import { customerPortalService } from '../../services/customerPortalApi';
import toast from 'react-hot-toast';
import { turkishIncludes } from '../../utils/turkishUtils';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';

// Hareket tipi çeviri fonksiyonu
const getMovementTypeLabel = (type) => {
  if (!type) return '-';
  const upperType = type.toUpperCase();
  if (upperType === 'PAYMENT') return 'Ödeme';
  if (upperType === 'COLLECTION') return 'Tahsilat';
  return type;
};

const CustomerAccountMovements = () => {
  const [currency, setCurrency] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateRange, setDateRange] = useState('last30');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Sıralama state'leri
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Sütun bazlı arama state'leri
  const [columnSearches, setColumnSearches] = useState({});
  const [activeSearchColumn, setActiveSearchColumn] = useState(null);
  
  // Görünüm modu
  const [viewMode, setViewMode] = useState('basit'); // 'basit' veya 'detayli'

  const debounceRef = useRef(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (currency) q.append('currency', currency);
      if (startDate) q.append('startDate', startDate);
      if (endDate) q.append('endDate', endDate);
      const res = await customerPortalService.get(`/account-ledger?${q.toString()}`);
      if (res.data.success) setRows(res.data.data || []);
      else toast.error('Hesap hareketleri alınamadı');
    } catch (e) {
      toast.error('Hesap hareketleri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [currency, startDate, endDate]);

  const scheduleLoad = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
  }, [load]);

  const handleDateRangeChange = useCallback((range) => {
    const now = new Date();
    const istanbulToday = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
    
    const format = (date) => {
      return date.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
    };
    
    let start = '';
    let end = '';

    switch (range) {
      case 'today': {
        start = end = format(istanbulToday);
        break;
      }
      case 'yesterday': {
        const y = new Date(istanbulToday);
        y.setDate(y.getDate() - 1);
        start = end = format(y);
        break;
      }
      case 'thisWeek': {
        const weekStart = new Date(istanbulToday);
        const day = weekStart.getDay();
        const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
        weekStart.setDate(diff);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        start = format(weekStart);
        end = format(weekEnd > istanbulToday ? istanbulToday : weekEnd);
        break;
      }
      case 'thisMonth': {
        const monthStart = new Date(istanbulToday.getFullYear(), istanbulToday.getMonth(), 1);
        const monthEnd = new Date(istanbulToday.getFullYear(), istanbulToday.getMonth() + 1, 0);
        const finalEnd = monthEnd > istanbulToday ? istanbulToday : monthEnd;
        start = format(monthStart);
        end = format(finalEnd);
        break;
      }
      case 'lastMonth': {
        const startMonth = new Date(istanbulToday.getFullYear(), istanbulToday.getMonth() - 1, 1);
        const endMonth = new Date(istanbulToday.getFullYear(), istanbulToday.getMonth(), 0);
        start = format(startMonth);
        end = format(endMonth);
        break;
      }
      case 'last30': {
        const start30 = new Date(istanbulToday);
        start30.setDate(start30.getDate() - 29);
        start = format(start30);
        end = format(istanbulToday);
        break;
      }
      case 'last7': {
        const start7 = new Date(istanbulToday);
        start7.setDate(start7.getDate() - 6);
        start = format(start7);
        end = format(istanbulToday);
        break;
      }
      case 'thisYear': {
        const yearStart = new Date(istanbulToday.getFullYear(), 0, 1);
        start = format(yearStart);
        end = format(istanbulToday);
        break;
      }
      case 'custom':
        setDateRange('custom');
        return;
      default:
        start = '';
        end = '';
    }

    setDateRange(range);
    setStartDate(start);
    setEndDate(end);
  }, []);
  
  // Tarih değiştiğinde otomatik yükle
  useEffect(() => {
    scheduleLoad();
  }, [startDate, endDate, scheduleLoad]);

  useEffect(() => { load(); }, [load]);

  // Varsayılan tarih filtresi: son 30 gün
  useEffect(() => {
    handleDateRangeChange('last30');
  }, [handleDateRangeChange]);

  // Sıralama fonksiyonu
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sütun arama modunu aç/kapat (önceki filtre değerleri korunur)
  const toggleColumnSearch = (column) => {
    openOrToggleColumnSearch({
      column,
      activeColumn: activeSearchColumn,
      setActiveColumn: setActiveSearchColumn,
      searches: columnSearches,
      setSearches: setColumnSearches
    });
  };

  const clearColumnSearch = (column) => {
    clearColumnSearchValue({
      column,
      activeColumn: activeSearchColumn,
      setActiveColumn: setActiveSearchColumn,
      setSearches: setColumnSearches
    });
  };

  // Sütun arama değerini güncelle
  const handleColumnSearchChange = (column, value) => {
    setColumnSearches(prev => ({ ...prev, [column]: value }));
  };

  // Filtrelenmiş ve sıralanmış satırlar
  const filteredAndSortedRows = useMemo(() => {
    let filtered = [...rows];

    // Sütun bazlı aramalar
    Object.keys(columnSearches).forEach(column => {
      const searchValue = columnSearches[column]?.trim();
      if (searchValue) {
        filtered = filtered.filter(row => {
          switch (column) {
            case 'date':
              return new Date(row.date).toLocaleDateString('tr-TR').includes(searchValue);
            case 'movementType':
              return turkishIncludes(row.movementTypeName || '', searchValue);
            case 'stockName':
              return turkishIncludes(row.stockName || '', searchValue);
            case 'color':
              return turkishIncludes(row.color || '', searchValue);
            case 'pattern':
              return turkishIncludes(row.pattern || '', searchValue);
            case 'variant':
              return turkishIncludes(row.variant || '', searchValue);
            case 'topCount':
              return String(row.topCount || 0).includes(searchValue);
            case 'grossQuantity':
              return String(row.grossQuantity || 0).includes(searchValue);
            case 'netQuantity':
              return String(row.netQuantity || 0).includes(searchValue);
            case 'meterQuantity':
              return String(row.meterQuantity || 0).includes(searchValue);
            case 'unitPrice':
              return String(row.unitPrice || 0).includes(searchValue);
            case 'totalAmount':
              return String(row.totalAmount || 0).includes(searchValue);
            case 'currencyCode':
              return turkishIncludes(row.currencyCode || '', searchValue);
            case 'debit':
              return String(row.debit || 0).includes(searchValue);
            case 'credit':
              return String(row.credit || 0).includes(searchValue);
            case 'balance':
              return String(row.balance || 0).includes(searchValue);
            case 'description':
              return turkishIncludes(row.description || '', searchValue);
            default:
              return true;
          }
        });
      }
    });

    // Sıralama
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal, bVal;

        switch (sortColumn) {
          case 'date':
            aVal = new Date(a.date).getTime();
            bVal = new Date(b.date).getTime();
            break;
          case 'movementType':
            aVal = (a.movementTypeName || '').toLowerCase();
            bVal = (b.movementTypeName || '').toLowerCase();
            break;
          case 'stockName':
            aVal = (a.stockName || '').toLowerCase();
            bVal = (b.stockName || '').toLowerCase();
            break;
          case 'color':
            aVal = (a.color || '').toLowerCase();
            bVal = (b.color || '').toLowerCase();
            break;
          case 'pattern':
            aVal = (a.pattern || '').toLowerCase();
            bVal = (b.pattern || '').toLowerCase();
            break;
          case 'variant':
            aVal = (a.variant || '').toLowerCase();
            bVal = (b.variant || '').toLowerCase();
            break;
          case 'topCount':
            aVal = parseFloat(a.topCount || 0);
            bVal = parseFloat(b.topCount || 0);
            break;
          case 'grossQuantity':
            aVal = parseFloat(a.grossQuantity || 0);
            bVal = parseFloat(b.grossQuantity || 0);
            break;
          case 'netQuantity':
            aVal = parseFloat(a.netQuantity || 0);
            bVal = parseFloat(b.netQuantity || 0);
            break;
          case 'meterQuantity':
            aVal = parseFloat(a.meterQuantity || 0);
            bVal = parseFloat(b.meterQuantity || 0);
            break;
          case 'unitPrice':
            aVal = parseFloat(a.unitPrice || 0);
            bVal = parseFloat(b.unitPrice || 0);
            break;
          case 'totalAmount':
            aVal = parseFloat(a.totalAmount || 0);
            bVal = parseFloat(b.totalAmount || 0);
            break;
          case 'currencyCode':
            aVal = (a.currencyCode || '').toLowerCase();
            bVal = (b.currencyCode || '').toLowerCase();
            break;
          case 'debit':
            aVal = parseFloat(a.debit || 0);
            bVal = parseFloat(b.debit || 0);
            break;
          case 'credit':
            aVal = parseFloat(a.credit || 0);
            bVal = parseFloat(b.credit || 0);
            break;
          case 'balance':
            aVal = parseFloat(a.balance || 0);
            bVal = parseFloat(b.balance || 0);
            break;
          case 'description':
            aVal = (a.description || '').toLowerCase();
            bVal = (b.description || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const result = aVal.localeCompare(bVal, 'tr');
          return sortDirection === 'asc' ? result : -result;
        } else {
          const result = aVal - bVal;
          return sortDirection === 'asc' ? result : -result;
        }
      });
    }

    return filtered;
  }, [rows, sortColumn, sortDirection, columnSearches]);

  // Tablo başlığı render fonksiyonları (admin panelindeki gibi)
  const renderTableHeaderLeft = (column, label) => (
    <ColumnSearchHeaderCell
      label={label}
      searchValue={columnSearches[column] || ''}
      isSearchActive={activeSearchColumn === column}
      isSorted={sortColumn === column}
      sortDirection={sortDirection}
      align="left"
      onSort={() => handleSort(column)}
      onOpenSearch={() => toggleColumnSearch(column)}
      onClearSearch={() => clearColumnSearch(column)}
      onSearchChange={(value) => handleColumnSearchChange(column, value)}
      onCloseSearchEdit={() => setActiveSearchColumn(null)}
      className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap"
    />
  );

  const renderTableHeaderRight = (column, label) => (
    <ColumnSearchHeaderCell
      label={label}
      searchValue={columnSearches[column] || ''}
      isSearchActive={activeSearchColumn === column}
      isSorted={sortColumn === column}
      sortDirection={sortDirection}
      align="right"
      onSort={() => handleSort(column)}
      onOpenSearch={() => toggleColumnSearch(column)}
      onClearSearch={() => clearColumnSearch(column)}
      onSearchChange={(value) => handleColumnSearchChange(column, value)}
      onCloseSearchEdit={() => setActiveSearchColumn(null)}
      className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap"
    />
  );

  const renderTableHeaderCenter = (column, label) => (
    <ColumnSearchHeaderCell
      label={label}
      searchValue={columnSearches[column] || ''}
      isSearchActive={activeSearchColumn === column}
      isSorted={sortColumn === column}
      sortDirection={sortDirection}
      align="center"
      onSort={() => handleSort(column)}
      onOpenSearch={() => toggleColumnSearch(column)}
      onClearSearch={() => clearColumnSearch(column)}
      onSearchChange={(value) => handleColumnSearchChange(column, value)}
      onCloseSearchEdit={() => setActiveSearchColumn(null)}
      className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap"
    />
  );

  const groupedRows = useMemo(() => {
    const bucket = {};
    filteredAndSortedRows.forEach(r => {
      const code = r.currencyCode || 'TL';
      if (!bucket[code]) bucket[code] = [];
      bucket[code].push(r);
    });
    const pref = ['TL','USD','EUR'];
    const keys = Object.keys(bucket).sort((a, b) => {
      const ia = pref.indexOf(a); const ib = pref.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1; if (ib === -1) return -1;
      return ia - ib;
    });
    return keys.map(k => ({ currency: k, rows: bucket[k] }));
  }, [filteredAndSortedRows]);

  // Toplam istatistikler hesapla (birimlere göre)
  const totalStats = useMemo(() => {
    let totalTop = 0;
    let totalMetre = 0;
    let totalKg = 0;
    let totalBorç = 0;
    let totalÖdeme = 0;

    filteredAndSortedRows.forEach(r => {
      totalTop += parseFloat(r.topCount || 0);
      totalBorç += parseFloat(r.debit || 0);
      totalÖdeme += parseFloat(r.credit || 0);
      
      // Net miktarı birimine göre topla
      const unitName = (r.unitName || r.unit?.name || r.unit?.shortName || '').toUpperCase();
      const netQuantity = parseFloat(r.netQuantity || 0);
      
      if (netQuantity > 0) {
        // Metre birimini kontrol et
        if (unitName.includes('METRE') || unitName.includes('M') || unitName === 'M' || unitName === 'MT') {
          totalMetre += netQuantity;
        }
        // Kg birimini kontrol et
        if (unitName.includes('KG') || unitName.includes('KILO') || unitName.includes('KILOGRAM') || unitName === 'KG') {
          totalKg += netQuantity;
        }
      }
      
      // Eğer meterQuantity direkt varsa onu da ekle
      if (r.meterQuantity) {
        totalMetre += parseFloat(r.meterQuantity || 0);
      }
    });

    return { totalTop, totalMetre, totalKg, totalBorç, totalÖdeme };
  }, [filteredAndSortedRows]);

  // Excel'e aktar fonksiyonu
  const exportToExcel = () => {
    if (filteredAndSortedRows.length === 0) {
      toast.error('Dışa aktarılacak veri bulunamadı');
      return;
    }

    try {
      const columnWidths = viewMode === 'basit'
        ? [
            { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 10 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 25 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }
          ]
        : [
            { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 15 },
            { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 25 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }
          ];

      const workbook = XLSX.utils.book_new();

      groupedRows.forEach(group => {
        let excelData = [];

        group.rows.forEach(row => {
          if (viewMode === 'basit') {
            excelData.push({
              'Tarih': new Date(row.date).toLocaleDateString('tr-TR'),
              'Hareket Tipi': getMovementTypeLabel(row.movementTypeName),
              'Stok Adı': row.stockName || '-',
              'Renk': row.color || '-',
              'Top': row.topCount || 0,
              'Net Miktar': row.netQuantity ? parseFloat(row.netQuantity).toFixed(2) : '0.00',
              'Birim Fiyat': row.unitPrice ? parseFloat(row.unitPrice).toFixed(2) : '0.00',
              'Toplam Tutar': row.totalAmount ? parseFloat(row.totalAmount).toFixed(2) : '0.00',
              'Döviz': row.currencyCode || '-',
              'Açıklama': row.description || '-',
              'Borç': row.debit > 0 ? parseFloat(row.debit).toFixed(2) : '-',
              'Alacak': row.credit > 0 ? parseFloat(row.credit).toFixed(2) : '-',
              'Bakiye': row.balance ? parseFloat(row.balance).toFixed(2) : '0.00'
            });
          } else {
            excelData.push({
              'Tarih': new Date(row.date).toLocaleDateString('tr-TR'),
              'Hareket Tipi': getMovementTypeLabel(row.movementTypeName),
              'Stok Adı': row.stockName || '-',
              'Renk': row.color || '-',
              'Desen': row.pattern || '-',
              'Varyant': row.variant || '-',
              'Top': row.topCount || 0,
              'Brüt Miktar': row.grossQuantity ? parseFloat(row.grossQuantity).toFixed(2) : '0.00',
              'Net Miktar': row.netQuantity ? parseFloat(row.netQuantity).toFixed(2) : '0.00',
              'Metre': row.meterQuantity ? parseFloat(row.meterQuantity).toFixed(2) : '0.00',
              'Birim Fiyat': row.unitPrice ? parseFloat(row.unitPrice).toFixed(2) : '0.00',
              'Toplam Tutar': row.totalAmount ? parseFloat(row.totalAmount).toFixed(2) : '0.00',
              'Döviz': row.currencyCode || '-',
              'Açıklama': row.description || '-',
              'Borç': row.debit > 0 ? parseFloat(row.debit).toFixed(2) : '-',
              'Alacak': row.credit > 0 ? parseFloat(row.credit).toFixed(2) : '-',
              'Bakiye': row.balance ? parseFloat(row.balance).toFixed(2) : '0.00'
            });
          }
        });

        if (excelData.length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(excelData);
          worksheet['!cols'] = columnWidths;
          const sheetName = group.currency.length > 31 ? group.currency.substring(0, 31) : group.currency;
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        }
      });

      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const viewModeTxt = viewMode === 'basit' ? 'Basit' : 'Detayli';
      const filename = `Hesap_Hareketleri_${viewModeTxt}_${dateStr}.xlsx`;

      XLSX.writeFile(workbook, filename);
      toast.success(`${filteredAndSortedRows.length} kayıt Excel'e aktarıldı`);
    } catch (error) {
      toast.error('Excel dosyası oluşturulurken hata oluştu');
    }
  };

  return (
    <CustomerPortalLayout title="Hesap Hareketleri" icon={FileText}>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filtreler ve Bilgi Kartları */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm sm:shadow p-2 sm:p-3 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-2 sm:gap-3 items-end">
            {/* Sol Taraf - Filtreler */}
            <div className="lg:col-span-3">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="min-w-[100px]">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-0.5">Döviz</label>
                  <select value={currency} onChange={e => setCurrency(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border dark:bg-gray-700 dark:border-gray-600 text-xs">
                    <option value="ALL">Tümü</option>
                    <option value="TL">TL</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-0.5">Tarih</label>
                  <select
                    value={dateRange}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDateRange(value);
                      if (value !== 'custom') {
                        handleDateRangeChange(value);
                      }
                    }}
                    className="w-full px-2 py-1.5 rounded-lg border dark:bg-gray-700 dark:border-gray-600 text-xs"
                  >
                    <option value="last7">Son 7 Gün</option>
                    <option value="today">Bugün</option>
                    <option value="yesterday">Dün</option>
                    <option value="thisWeek">Bu Hafta</option>
                    <option value="thisMonth">Bu Ay</option>
                    <option value="lastMonth">Geçen Ay</option>
                    <option value="last30">Son 30 Gün</option>
                    <option value="thisYear">Bu Yıl</option>
                    <option value="custom">Özel Tarih</option>
                  </select>
                </div>
                {dateRange === 'custom' && (
                  <>
                    <div className="min-w-[120px]">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-0.5">Başlangıç</label>
                      <input 
                        type="date" 
                        value={startDate} 
                        onChange={e => {
                          setStartDate(e.target.value);
                          if (e.target.value && endDate) {
                            scheduleLoad();
                          }
                        }} 
                        className="w-full px-2 py-1.5 rounded-lg border dark:bg-gray-700 dark:border-gray-600 text-xs" 
                      />
                    </div>
                    <div className="min-w-[120px]">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-0.5">Bitiş</label>
                      <input 
                        type="date" 
                        value={endDate} 
                        onChange={e => {
                          setEndDate(e.target.value);
                          if (startDate && e.target.value) {
                            scheduleLoad();
                          }
                        }} 
                        className="w-full px-2 py-1.5 rounded-lg border dark:bg-gray-700 dark:border-gray-600 text-xs" 
                      />
                    </div>
                  </>
                )}
                <div className="flex gap-1.5">
                  <button onClick={load} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors text-xs">
                    Filtrele
                  </button>
                  <button
                    onClick={exportToExcel}
                    disabled={loading || filteredAndSortedRows.length === 0}
                    className="flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors shadow-sm disabled:cursor-not-allowed text-xs"
                  >
                    <Download className="w-3 h-3" />
                    <span className="text-xs font-medium">Excel</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Sağ Taraf - Bilgi Kartları */}
            <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5 sm:gap-2">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-1.5 sm:p-2 border border-blue-200 dark:border-blue-800">
                <p className="text-[9px] sm:text-[10px] font-medium text-blue-700 dark:text-blue-300 mb-0.5">Toplam Top</p>
                <p className="text-xs sm:text-sm font-bold text-blue-800 dark:text-blue-200 tabular-nums">
                  {totalStats.totalTop.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-1.5 sm:p-2 border border-purple-200 dark:border-purple-800">
                <p className="text-[9px] sm:text-[10px] font-medium text-purple-700 dark:text-purple-300 mb-0.5">Toplam Metre</p>
                <p className="text-xs sm:text-sm font-bold text-purple-800 dark:text-purple-200 tabular-nums">
                  {totalStats.totalMetre.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-lg p-1.5 sm:p-2 border border-indigo-200 dark:border-indigo-800">
                <p className="text-[9px] sm:text-[10px] font-medium text-indigo-700 dark:text-indigo-300 mb-0.5">Toplam Kg</p>
                <p className="text-xs sm:text-sm font-bold text-indigo-800 dark:text-indigo-200 tabular-nums">
                  {totalStats.totalKg.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20 rounded-lg p-1.5 sm:p-2 border border-rose-200 dark:border-rose-800">
                <p className="text-[9px] sm:text-[10px] font-medium text-rose-700 dark:text-rose-300 mb-0.5">Toplam Borç</p>
                <p className="text-xs sm:text-sm font-bold text-rose-800 dark:text-rose-200 tabular-nums">
                  {totalStats.totalBorç.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-lg p-1.5 sm:p-2 border border-emerald-200 dark:border-emerald-800">
                <p className="text-[9px] sm:text-[10px] font-medium text-emerald-700 dark:text-emerald-300 mb-0.5">Toplam Ödeme</p>
                <p className="text-xs sm:text-sm font-bold text-emerald-800 dark:text-emerald-200 tabular-nums">
                  {totalStats.totalÖdeme.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">

            {/* Görünüm Toggle */}
            <div className="flex gap-2 items-center">
              <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setViewMode('basit')}
                  className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
                    viewMode === 'basit'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="text-sm font-medium">Basit</span>
                </button>
                <button
                  onClick={() => setViewMode('detayli')}
                  className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
                    viewMode === 'detayli'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  <span className="text-sm font-medium">Detaylı</span>
                </button>
              </div>
            </div>

            {/* Tablo */}
            <div className={`overflow-auto rounded border dark:border-gray-700 max-h-[70vh] ${viewMode === 'detayli' ? 'overflow-x-auto' : ''}`}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                  <tr>
                    {viewMode === 'basit' ? (
                      <>
                        {renderTableHeaderLeft('date', 'Tarih')}
                        {renderTableHeaderLeft('movementType', 'Hareket Tipi')}
                        {renderTableHeaderLeft('stockName', 'Stok Adı')}
                        {renderTableHeaderLeft('color', 'Renk')}
                        {renderTableHeaderCenter('topCount', 'Top')}
                        {renderTableHeaderCenter('netQuantity', 'Net Miktar')}
                        {renderTableHeaderRight('unitPrice', 'Birim Fiyat')}
                        {renderTableHeaderRight('totalAmount', 'Toplam Tutar')}
                        {renderTableHeaderCenter('currencyCode', 'Döviz')}
                        {renderTableHeaderLeft('description', 'Açıklama')}
                        {renderTableHeaderRight('debit', 'Borç')}
                        {renderTableHeaderRight('credit', 'Alacak')}
                        {renderTableHeaderRight('balance', 'Bakiye')}
                      </>
                    ) : (
                      <>
                        {renderTableHeaderLeft('date', 'Tarih')}
                        {renderTableHeaderLeft('movementType', 'Hareket Tipi')}
                        {renderTableHeaderLeft('stockName', 'Stok Adı')}
                        {renderTableHeaderLeft('color', 'Renk')}
                        {renderTableHeaderLeft('pattern', 'Desen')}
                        {renderTableHeaderLeft('variant', 'Varyant')}
                        {renderTableHeaderCenter('topCount', 'Top')}
                        {renderTableHeaderCenter('grossQuantity', 'Brüt Miktar')}
                        {renderTableHeaderCenter('netQuantity', 'Net Miktar')}
                        {renderTableHeaderCenter('meterQuantity', 'Metre')}
                        {renderTableHeaderRight('unitPrice', 'Birim Fiyat')}
                        {renderTableHeaderRight('totalAmount', 'Toplam Tutar')}
                        {renderTableHeaderCenter('currencyCode', 'Döviz')}
                        {renderTableHeaderLeft('description', 'Açıklama')}
                        {renderTableHeaderRight('debit', 'Borç')}
                        {renderTableHeaderRight('credit', 'Alacak')}
                        {renderTableHeaderRight('balance', 'Bakiye')}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                  {loading ? (
                    <tr><td className="px-4 py-6 text-center" colSpan={viewMode === 'basit' ? 13 : 17}>Yükleniyor...</td></tr>
                  ) : filteredAndSortedRows.length === 0 ? (
                    <tr><td className="px-4 py-6 text-center" colSpan={viewMode === 'basit' ? 13 : 17}>Kayıt bulunamadı</td></tr>
                  ) : (
                    groupedRows.flatMap(group => {
                      const headerRow = (
                        <tr key={`grp-${group.currency}`} className="bg-gray-50 dark:bg-gray-800">
                          <td colSpan={viewMode === 'basit' ? 13 : 17} className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300">{group.currency} işlemler</td>
                        </tr>
                      );
                      const dataRows = group.rows.map(t => {
                        const displayBalance = t.balance;
                        const balanceColor = displayBalance > 0 ? 'text-emerald-600 dark:text-emerald-400' : displayBalance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-800 dark:text-gray-200';
                        
                        return (
                          <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            {viewMode === 'basit' ? (
                              <>
                                <td className="px-2 py-1 text-xs whitespace-nowrap text-left">{new Date(t.date).toLocaleDateString('tr-TR')}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap max-w-[120px] truncate text-left" title={getMovementTypeLabel(t.movementTypeName)}>{getMovementTypeLabel(t.movementTypeName)}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap max-w-[150px] truncate text-left" title={t.stockName}>{t.stockName}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap max-w-[80px] truncate text-left">{t.color || '-'}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.topCount || 0}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.netQuantity.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums font-medium">{t.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left">{t.currencyCode}</td>
                                <td className="px-2 py-1 text-xs text-left max-w-[200px] truncate" title={t.description || '-'}>{t.description || '-'}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums text-emerald-600 dark:text-emerald-400">{t.debit > 0 ? t.debit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums text-rose-600 dark:text-rose-400">{t.credit > 0 ? t.credit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                                <td className={`px-2 py-1 text-xs text-left tabular-nums font-medium ${balanceColor}`}>
                                  {displayBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-2 py-1 text-xs whitespace-nowrap text-left">{new Date(t.date).toLocaleDateString('tr-TR')}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap text-left">{getMovementTypeLabel(t.movementTypeName)}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap text-left">{t.stockName}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap text-left">{t.color}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap text-left">{t.pattern}</td>
                                <td className="px-2 py-1 text-xs whitespace-nowrap text-left">{t.variant}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.topCount}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.grossQuantity.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.netQuantity.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.meterQuantity.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums">{t.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums font-medium">{t.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1 text-xs text-left">{t.currencyCode}</td>
                                <td className="px-2 py-1 text-xs text-left max-w-[200px] truncate" title={t.description || '-'}>{t.description || '-'}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums text-emerald-600 dark:text-emerald-400">{t.debit > 0 ? t.debit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                                <td className="px-2 py-1 text-xs text-left tabular-nums text-rose-600 dark:text-rose-400">{t.credit > 0 ? t.credit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                                <td className={`px-2 py-1 text-xs text-left tabular-nums font-medium ${balanceColor}`}>
                                  {displayBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      });
                      return [headerRow, ...dataRows];
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
      </main>
    </CustomerPortalLayout>
  );
};

export default CustomerAccountMovements;
